import { Box, Dialog, DialogActions, DialogContent } from "@mui/material";
import { DialogTitleWithCloseIcon } from "../../../../../common/dialog/title/DialogTitleWithCloseIcon";
import Button from "@mui/material/Button";
import { IPersonBadge } from "../../../../../../common/types";
import { useTranslation } from "react-i18next";
import { useCompanyBadgeActions } from "../hooks/useCompanyBadgeActions";
import { FilterSetters } from "../../../../types";
import { deactivateDialogStyles, useStyles } from "./DeactivateDialog.styles";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { ThemedTable } from "../../../../../common/tables/ThemedTable";
import {
  muiDatatableDeactivationModal,
  muiDatatableDeactivationModalWithScroll
} from "../../../../../dashboard/reports/components/table/utils/theme";
import { useDeactivateTable } from "./hooks/useDeactivateTable";

type Props = {
  open: boolean;
  onClose: () => void;
  badgesToDeactivate: IPersonBadge[];
  deselectRows: () => void;
  filterSetters: FilterSetters;
};

export const DeactivateDialog = ({ badgesToDeactivate, open, onClose, deselectRows, filterSetters }: Props) => {
  const { t } = useTranslation("common");
  const { deactivateBadges, loading } = useCompanyBadgeActions();
  const [reasonNote, setReasonNote] = useState('');
  const { options, columns, tableData } = useDeactivateTable(badgesToDeactivate);
  const styles = useStyles();

  const handleClose = () => {
    if (loading) {
      return;
    }
    setReasonNote('');
    onClose();
  };

  const onSubmit = async () => {
    await deactivateBadges(badgesToDeactivate, reasonNote);
    handleClose();
    filterSetters.deselectFilters();
    deselectRows();
  };

  const tableTheme = columns.length < 4 ? muiDatatableDeactivationModalWithScroll : muiDatatableDeactivationModal;

  return (
    <Dialog open={open} sx={deactivateDialogStyles.container}>
      <DialogTitleWithCloseIcon
        title={t("companyPage.deactivateDialog.deactivateXBadges", { badgeCount: badgesToDeactivate.length })}
        handleClose={handleClose}
        className={styles.title}
      />
      <DialogContent sx={deactivateDialogStyles.content}>
        <Box sx={deactivateDialogStyles.subtitle}>
          {t("companyPage.deactivateDialog.subtitle", { badgeCount: badgesToDeactivate.length })}
        </Box>
        <Box sx={deactivateDialogStyles.subtitle}>
          {t("companyPage.deactivateDialog.warningMessage")}
        </Box>
        <ThemedTable title={""} theme={tableTheme} options={options} cols={columns} tableData={tableData} />
        <Box>
          <Box sx={deactivateDialogStyles.reasonLabel}>{t("companyPage.deactivateDialog.deactivationReason.label")}</Box>
          <TextField
            variant="outlined"
            sx={deactivateDialogStyles.reasonInputField}
            value={reasonNote}
            onChange={(e) => setReasonNote(e.target.value)}
            placeholder={t("companyPage.deactivateDialog.deactivationReason.placeholder")}
            inputProps={{ maxLength: 255 }}
            rows={2}
            fullWidth
            multiline
          />
        </Box>
      </DialogContent>
      <DialogActions sx={deactivateDialogStyles.buttons}>
        <Button disabled={loading} onClick={handleClose}>
          {t("common.buttonLabels.cancel")}
        </Button>
        <Button disabled={loading || !reasonNote} onClick={onSubmit} variant="contained">
          {t("common.buttonLabels.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
